import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BundleOrRecyclingField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateBundleOrRecycling';
import { BundleOrRecycling } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimate';
import { RootState } from 'app/AppStore';
import {
  getBundlesOrRecycling,
  removeBundleOrRecycling,
  updateBundleOrRecycling,
} from 'domains/estimate/Estimate.store';
import { getCurrency } from 'domains/user';
import BundleOrRecyclingAutocomplete from 'pages/EstimatePage/TableSection/BundleOrRecyclingAutocomplete';
import {
  DiscountInput,
  EstimatePriceInput,
  GetTitle,
  QuantityInput,
  VatInput,
} from 'pages/EstimatePage/TableSection/tableComponents';
import { TableProps } from 'pages/EstimatePage/TableSection/TableSection';
import { Flex } from 'UI';
import { textFormatter } from 'utils';
import { STable } from './TableSection.styled';

const BundleOrRecyclingTable = ({ estimateId }: TableProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currency = useSelector(getCurrency);
  const data = useSelector((state: RootState) => getBundlesOrRecycling(state, estimateId));

  function removeRow(rowId: string) {
    dispatch(removeBundleOrRecycling(rowId));
  }

  const handleUpdate = (itemId: string, field: BundleOrRecyclingField) => (newValue: string) => {
    dispatch(updateBundleOrRecycling({ itemId, field, newValue }));
  };

  const tableColumns = [
    {
      title: GetTitle(t('common.code', 'Code')),
      dataIndex: 'code',
      render: function rowSelector(code: string, row: BundleOrRecycling) {
        return (
          <BundleOrRecyclingAutocomplete
            value={code ?? ''}
            saveFunction={handleUpdate(row.itemId, 'CODE')}
            rowId={row.itemId}
            fieldId={'CODE'}
            removeRow={() => removeRow(row.itemId)}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.designation', 'Designation')),
      dataIndex: 'name',
      width: 300,
      render: function rowSelector(name: string, row: BundleOrRecycling) {
        return (
          <BundleOrRecyclingAutocomplete
            value={name ?? ''}
            saveFunction={handleUpdate(row.itemId, 'NAME')}
            rowId={row.itemId}
            fieldId={'NAME'}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.price.unit_price', 'Unit Price')),
      dataIndex: 'unitPrice',
      render: function rowSelector(unitPrice: string, row: BundleOrRecycling) {
        return <EstimatePriceInput value={unitPrice} saveFunction={handleUpdate(row.itemId, 'UNIT_PRICE')} />;
      },
    },
    {
      title: GetTitle(t('common.quantity', 'Quantity')),
      dataIndex: 'quantity',
      width: '5vw',
      render: function rowSelector(quantity: string, row: BundleOrRecycling) {
        return (
          <QuantityInput
            value={Number(quantity)}
            saveFunction={handleUpdate(row.itemId, 'QUANTITY')}
            removeRow={() => removeRow(row.itemId)}
            design={'v2'}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.discount', 'Discount')),
      dataIndex: 'discount',
      width: '10vw',
      render: function rowSelector(discount: string, row: BundleOrRecycling) {
        return (
          <DiscountInput
            value={discount}
            saveFunction={handleUpdate(row.itemId, 'DISCOUNT')}
            discountType={row.discountType}
            saveDiscountType={handleUpdate(row.itemId, 'DISCOUNT_TYPE')}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.price.vat_excl_price', 'VAT excl.Price')),
      dataIndex: 'priceVatExcluded',
      render: function rowSelector(vatExclPrice: string) {
        return (
          <Flex justify={'center'} align={'center'}>
            {textFormatter.formatCurrency(isNaN(Number(vatExclPrice)) ? 0 : vatExclPrice, currency)}
          </Flex>
        );
      },
    },
    {
      title: GetTitle(t('common.price.vat.title', 'VAT')),
      dataIndex: 'vatPercentage',
      width: '5vw',
      render: function rowSelector(vat: number, row: BundleOrRecycling) {
        return <VatInput value={vat} saveFunction={handleUpdate(row.itemId, 'VAT_PERCENTAGE')} />;
      },
    },
  ];

  if (data.length === 0) {
    return null;
  }
  return (
    <STable<BundleOrRecycling>
      columns={tableColumns}
      dataSource={data}
      pagination={false}
      rowKey={(row) => row.itemId}
      size={'large'}
    />
  );
};

export default BundleOrRecyclingTable;
